import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Reusable unsaved-change navigation guard for forms and editors.
 *
 * Lifted from the LandingPageEditor's inline guard so every dirty-state form
 * (WorkOrderForm, JournalEntries, LaborRateConfig, SystemConfig, …) can adopt
 * one hardened implementation instead of hand-rolling `beforeunload` + a
 * confirm dialog each time.
 *
 * Behaviour:
 *   1. Browser-level: while `dirty === true`, a `beforeunload` handler makes
 *      the browser prompt on tab close / reload / external navigation.
 *   2. In-app: a same-tab `popstate` (browser Back) while dirty is captured —
 *      the target is re-pushed so the user stays put, and surfaced as
 *      `pendingNavigation` so the caller can render a
 *      "Discard changes / Keep editing" dialog.
 *   3. Programmatic: call `requestNavigate(to)` instead of `navigate(to)` for
 *      in-app links (Cancel buttons, breadcrumbs) so they route through the
 *      same guard.
 *
 * The hook does NOT own the dirty flag — the form owns it (typically
 * `formState.isDirty` from react-hook-form) and threads it in via `dirty`.
 * On confirm the caller should clear its dirty flag, then call
 * `confirmNavigation()` to perform the deferred navigation.
 *
 * Depends only on `useLocation`/`useNavigate`, so it works with any
 * react-router v6/v7 tree without requiring a data router (`useBlocker`).
 */

// ─── Pure-logic state machine (unit-testable without a renderer) ───────────

export interface GuardState {
  pendingNavigation: string | null;
}

export const INITIAL_GUARD_STATE: GuardState = { pendingNavigation: null };

/**
 * Decide whether a requested navigation should proceed now or be deferred
 * behind a confirmation. Returns `'navigate'` when the form is clean (fire
 * immediately) or `'pend'` when dirty (stash the target).
 */
export function requestNavigateAction(
  target: string,
  dirty: boolean,
  prev: GuardState,
): { state: GuardState; effect: 'navigate' | 'pend' } {
  if (!dirty) return { state: prev, effect: 'navigate' };
  return { state: { pendingNavigation: target }, effect: 'pend' };
}

/** Clear the pending entry and return the path to navigate to (or null). */
export function confirmAction(prev: GuardState): {
  state: GuardState;
  target: string | null;
} {
  return { state: INITIAL_GUARD_STATE, target: prev.pendingNavigation };
}

/** Clear the pending entry (user chose to keep editing). */
export function cancelAction(): GuardState {
  return INITIAL_GUARD_STATE;
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export interface UnsavedChangeGuardApi {
  /** Mirrors the `dirty` argument for convenience at the call site. */
  dirty: boolean;
  /** `null` until the user attempts to navigate away with unsaved changes. */
  pendingNavigation: string | null;
  /** Perform the deferred navigation (call after clearing the dirty flag). */
  confirmNavigation: () => void;
  /** Dismiss the prompt and keep the user on the form. */
  cancelNavigation: () => void;
  /** Guarded programmatic navigation — use instead of `navigate(to)`. */
  requestNavigate: (to: string) => void;
}

export function useUnsavedChangeGuard(dirty: boolean): UnsavedChangeGuardApi {
  const location = useLocation();
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);
  // The route the form was mounted on; popstate away from it is intercepted.
  const formPathRef = useRef(location.pathname);

  // beforeunload — covers tab close / reload / external links.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore the string but still require `returnValue` be
      // set for the native prompt to fire.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // In-app back-button (popstate) interception while dirty.
  useEffect(() => {
    if (!dirty) return;
    const handler = () => {
      const target = window.location.pathname + window.location.search;
      if (target !== formPathRef.current) {
        setPending(target);
        // Re-push the form's path so the user stays put until they decide.
        window.history.pushState(null, '', formPathRef.current);
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [dirty]);

  const confirmNavigation = useCallback(() => {
    const { target } = confirmAction({ pendingNavigation: pending });
    if (target) {
      navigate(target);
      setPending(null);
    }
  }, [pending, navigate]);

  const cancelNavigation = useCallback(() => {
    setPending(null);
  }, []);

  const requestNavigate = useCallback(
    (to: string) => {
      const { effect } = requestNavigateAction(to, dirty, {
        pendingNavigation: pending,
      });
      if (effect === 'navigate') {
        navigate(to);
      } else {
        setPending(to);
      }
    },
    [dirty, pending, navigate],
  );

  return {
    dirty,
    pendingNavigation: pending,
    confirmNavigation,
    cancelNavigation,
    requestNavigate,
  };
}
